import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Grid3x3, PenTool, Package, User, ShoppingBag, Search } from 'lucide-react';
import { useCartStore } from '@/store/cartStore';
import { useAuthStore } from '@/store/authStore';
import { buildLoginHref } from '@/lib/auth';
import { haptic, initNativeEvents } from '@/lib/appBridge';
import SearchBar from './SearchBar';

/* ============================================================================
   Native app shell — rendered INSTEAD of the website chrome when the page runs
   inside the Android app (html.is-app). Browsers never mount this.
   ============================================================================ */

// Top-level destinations: tab bar visible, no back arrow.
const TOP_LEVEL = ['/', '/categories', '/design', '/orders', '/account'];

// Flows that own the bottom of the screen (sticky CTAs, forms) — no tab bar.
const NO_TAB_BAR = [/^\/products\//, /^\/checkout/, /^\/cart/, /^\/login/, /^\/verify/, /^\/design\/[^/]+/, /^\/orders\/.+/, /^\/account\/.+/];

const FIXED_TITLES: Record<string, string> = {
  '/cart': 'Cart', '/checkout': 'Checkout', '/categories': 'Categories', '/design': 'Design',
  '/orders': 'My Orders', '/account': 'Account', '/help': 'Help', '/login': 'Sign in',
};

// Route patterns for screens whose name comes from data (a category, a
// product…). Shown until the page's own heading arrives.
const PATTERN_TITLES: [RegExp, string][] = [
  [/^\/categories\/[^/]+/, 'Category'],
  [/^\/products\/[^/]+/, 'Product'],
  [/^\/design\/editor\//, 'Design studio'],
  [/^\/design\/[^/]+/, 'Choose a Template'],
  [/^\/orders\/[^/]+/, 'Order details'],
  [/^\/account\/[^/]+/, 'Account'],
  [/^\/search/, 'Search'],
  [/^\/(about|contact|terms|privacy|shipping|careers|help)/, ''],
];

/**
 * Title for the CURRENT route only. It never reads document.title: lazily
 * loaded pages set it late, so at navigation time it still held the previous
 * page's name ("Sign In" on Visiting Cards, "Your Orders" on the template
 * chooser).
 */
export function routeTitle(pathname: string): string {
  if (FIXED_TITLES[pathname]) return FIXED_TITLES[pathname];
  for (const [re, t] of PATTERN_TITLES) if (re.test(pathname)) return t;
  return 'EsyPrints';
}

function AppTopBar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { itemCount } = useCartStore();
  const { customer } = useAuthStore();
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [title, setTitle] = useState('');
  const isHome = pathname === '/';
  const isTop = TOP_LEVEL.includes(pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Title = the current route's name, refined by the CURRENT page's own main
  // heading once it renders (a category or product name). Reset on every
  // route change so nothing from the previous page can leak in. Once the bar
  // shows the heading, the in-page copy is hidden (data-in-appbar) so
  // "My Orders" doesn't appear twice.
  useEffect(() => {
    const base = routeTitle(pathname);
    setTitle(base);
    const main = document.querySelector<HTMLElement>('.app-main');
    const update = () => {
      const h1 = main?.querySelector<HTMLElement>('h1');
      const h1Text = h1?.textContent?.trim();
      if (!h1 || !h1Text) return;
      if (h1Text.length <= 40) {
        if (!FIXED_TITLES[pathname]) setTitle(h1Text);
        h1.setAttribute('data-in-appbar', '');
      }
    };
    update();
    // The heading renders once the lazy page and its data arrive.
    const mo = main ? new MutationObserver(update) : null;
    mo?.observe(main as HTMLElement, { childList: true, subtree: true, characterData: true });
    const stop = window.setTimeout(() => mo?.disconnect(), 6000);
    return () => { mo?.disconnect(); clearTimeout(stop); };
  }, [pathname]);

  useEffect(() => { setSearchOpen(false); }, [pathname]);
  useEffect(() => {
    if (!searchOpen) return;
    const t = window.setTimeout(() => document.querySelector<HTMLInputElement>('.app-search .search-bar input')?.focus(), 50);
    return () => clearTimeout(t);
  }, [searchOpen]);

  const firstName = customer?.name?.split(' ')[0];

  return (
    <>
    <header className={`app-topbar ${scrolled ? 'is-scrolled' : ''}`}>
      {isHome ? (
        <div className="app-topbar-title">
          {firstName ? <>Hi, {firstName} 👋</> : <span className="app-brand">Esy<b>Prints</b></span>}
        </div>
      ) : (
        <>
          {!isTop && (
            <button className="app-icon-btn" aria-label="Back" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}>
              <ArrowLeft size={22} />
            </button>
          )}
          <div className="app-topbar-title">{title}</div>
        </>
      )}
      <div className="app-topbar-actions">
        <button type="button" className="app-icon-btn" aria-label="Search" onClick={() => setSearchOpen(true)}><Search size={21} /></button>
        <Link to="/cart" className="app-icon-btn" aria-label="Cart">
          <ShoppingBag size={21} />
          {itemCount > 0 && <span className="app-badge">{itemCount > 99 ? '99+' : itemCount}</span>}
        </Link>
      </div>
    </header>
    {searchOpen && (
      <div className="app-search" role="dialog" aria-label="Search">
        <div className="app-search-bar">
          <button type="button" className="app-icon-btn" aria-label="Close search" onClick={() => setSearchOpen(false)}>
            <ArrowLeft size={22} />
          </button>
          <div className="app-search-field"><SearchBar /></div>
        </div>
      </div>
    )}
    </>
  );
}

function AppTabBar() {
  const location = useLocation();
  const { pathname } = location;
  const { isAuthenticated } = useAuthStore();
  const loginHref = buildLoginHref(pathname, location.search);
  const tabs = [
    { to: '/', label: 'Home', icon: Home, on: pathname === '/' },
    { to: '/categories', label: 'Categories', icon: Grid3x3, on: pathname.startsWith('/categories') },
    { to: '/design', label: 'Design', icon: PenTool, on: pathname === '/design' },
    { to: isAuthenticated ? '/orders' : loginHref, label: 'Orders', icon: Package, on: pathname.startsWith('/orders') },
    { to: isAuthenticated ? '/account' : loginHref, label: 'Account', icon: User, on: pathname.startsWith('/account') },
  ];
  return (
    <nav className="app-tabbar" aria-label="Primary">
      {tabs.map(({ to, label, icon: Icon, on }) => (
        <Link key={label} to={to} onClick={() => { haptic('light'); if (on) window.scrollTo({ top: 0, behavior: 'smooth' }); }} className={`app-tab ${on ? 'is-active' : ''}`} aria-current={on ? 'page' : undefined}>
          <Icon size={23} strokeWidth={on ? 2.4 : 1.8} fill={on ? 'currentColor' : 'none'} fillOpacity={on ? 0.15 : 0} />
          <span>{label}</span>
        </Link>
      ))}
    </nav>
  );
}

export default function AppShell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const showTabs = !NO_TAB_BAR.some((re) => re.test(pathname));

  useEffect(() => { initNativeEvents((p) => navigate(p)); }, [navigate]);
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  return (
    <>
      <AppTopBar />
      <main className={`site-main app-main ${showTabs ? 'has-tabs' : ''}`}>{children}</main>
      {showTabs && <AppTabBar />}
    </>
  );
}
